/**
 * TREASURED — Vinted-style navigation behaviour (isolated add-on)
 * ---------------------------------------------------------------
 * Loaded only when the "Vinted-style menu" block setting is enabled.
 * Defines two self-contained custom elements:
 *   <vinted-menu>   desktop tabs + two-pane dropdown (hover / keyboard)
 *   <vinted-drawer> mobile drill-down drawer (Category → Sub 1 → Sub 2)
 *
 * No imports from the theme so the feature can be removed cleanly.
 * The navigation data itself is rendered by Liquid from the store's
 * existing main-menu — this file never touches URLs.
 */

const OPEN_DELAY = 60; // ms — small intent delay before a tab opens its panel
const RAIL_DELAY = 90; // ms — hover intent on the left rail (prevents pane flicker when crossing rows diagonally)
const CLOSE_DELAY = 220; // ms — grace period after the pointer leaves the menu/panel
const RESIZE_DEBOUNCE = 120;

const isTouchDevice = () => 'ontouchstart' in window && navigator.maxTouchPoints > 0;
const isRTL = () => (document.documentElement.getAttribute('dir') || getComputedStyle(document.documentElement).direction) === 'rtl';

/* =========================================================================
   Desktop menu
   ========================================================================= */
class VintedMenu extends HTMLElement {
  #openTimer = null;
  #closeTimer = null;
  #railTimer = null;
  #resizeTimer = null;
  #drawerSwitchWidth = null;
  #openItem = null;
  #suppressFocusOpen = false;

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-vn-item]'));
    this.triggers = Array.from(this.querySelectorAll('[data-vn-trigger]'));

    for (const item of this.items) {
      item.addEventListener('pointerenter', this.#onItemEnter);
    }
    // Rail links switch the right-hand pane on hover (pointerenter/leave do not bubble)
    for (const link of this.querySelectorAll('[data-vn-rail]')) {
      link.addEventListener('pointerenter', this.#onRailEnter);
      link.addEventListener('pointerleave', this.#onRailLeave);
    }

    this.addEventListener('pointerenter', this.#cancelClose);
    this.addEventListener('pointerleave', this.#scheduleClose);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    window.addEventListener('resize', this.#onResize);

    // Measure only once vinted-menu.css has actually been applied (it is linked from the
    // body, so the module can run before the stylesheet arrives) and again after fonts load.
    this.#whenStyled(() => this.#syncMenuStyle());
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.#whenStyled(() => this.#syncMenuStyle())).catch(() => {});
    }
  }

  #whenStyled(callback, attempt = 0) {
    const list = this.querySelector('.vn__list');
    const styled = list && getComputedStyle(list).display === 'flex';
    if (styled || attempt > 40) {
      callback();
      return;
    }
    window.setTimeout(() => this.#whenStyled(callback, attempt + 1), 50);
  }

  disconnectedCallback() {
    for (const item of this.items || []) item.removeEventListener('pointerenter', this.#onItemEnter);
    for (const link of this.querySelectorAll('[data-vn-rail]')) {
      link.removeEventListener('pointerenter', this.#onRailEnter);
      link.removeEventListener('pointerleave', this.#onRailLeave);
    }
    this.removeEventListener('pointerenter', this.#cancelClose);
    this.removeEventListener('pointerleave', this.#scheduleClose);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#railTimer);
    clearTimeout(this.#resizeTimer);
  }

  /* ---- open / close ---------------------------------------------------- */

  /** @param {HTMLElement} item */
  open(item) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (!item || !item.querySelector('[data-vn-panel]')) {
      this.close();
      return;
    }
    if (this.#openItem === item) return;

    this.close({ keepState: true });

    item.setAttribute('data-open', '');
    const trigger = item.querySelector('[data-vn-trigger]');
    trigger?.setAttribute('aria-expanded', 'true');
    this.setAttribute('data-open', '');
    this.#openItem = item;
    this.#measureNavStart();

    // Always start from the first Subcategory 1 entry (predictable, like Vinted)
    const firstRail = item.querySelector('[data-vn-rail]');
    if (firstRail) this.activateRail(firstRail);
  }

  close({ keepState = false } = {}) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#railTimer);
    const item = this.#openItem;
    if (item) {
      item.removeAttribute('data-open');
      item.querySelector('[data-vn-trigger]')?.setAttribute('aria-expanded', 'false');
    }
    this.#openItem = null;
    if (!keepState) this.removeAttribute('data-open');
  }

  /** Switch the right-hand pane to the Subcategory 2 list of a rail link. */
  activateRail(railLink) {
    const item = railLink.closest('[data-vn-item]');
    if (!item) return;
    const target = railLink.getAttribute('data-vn-rail');
    for (const link of item.querySelectorAll('[data-vn-rail]')) {
      link.classList.toggle('is-active', link === railLink);
    }
    for (const pane of item.querySelectorAll('[data-vn-pane]')) {
      pane.hidden = pane.getAttribute('data-vn-pane') !== target;
    }
  }

  /* ---- pointer --------------------------------------------------------- */

  #onItemEnter = (event) => {
    const item = event.currentTarget;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    if (!item.querySelector('[data-vn-panel]')) {
      // hovering a plain link (e.g. SELL FOR FREE) closes any open panel
      this.#openTimer = setTimeout(() => this.close(), OPEN_DELAY);
      return;
    }
    const delay = this.#openItem ? 0 : OPEN_DELAY;
    this.#openTimer = setTimeout(() => this.open(item), delay);
  };

  #onRailEnter = (event) => {
    const link = event.currentTarget;
    clearTimeout(this.#railTimer);
    if (link.classList.contains('is-active')) return;
    // Short intent delay: crossing a row on the way to the right-hand pane must not switch panes
    this.#railTimer = setTimeout(() => this.activateRail(link), RAIL_DELAY);
  };

  #onRailLeave = () => {
    clearTimeout(this.#railTimer);
  };

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
  };

  #scheduleClose = () => {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
  };

  #onDocumentPointerDown = (event) => {
    if (this.#openItem && !this.contains(event.target)) this.close();
  };

  /* ---- keyboard / focus ------------------------------------------------ */

  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    clearTimeout(this.#closeTimer);

    if (target.hasAttribute('data-vn-trigger')) {
      if (this.#suppressFocusOpen) return; // focus returned after Escape — keep closed
      const item = target.closest('[data-vn-item]');
      if (item?.querySelector('[data-vn-panel]')) this.open(item);
      else this.close();
      return;
    }
    if (target.hasAttribute('data-vn-rail')) {
      this.activateRail(target);
    }
  };

  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    // focus left the whole navigation
    this.#closeTimer = setTimeout(() => this.close(), 10);
  };

  #onKeyDown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const rtl = isRTL();
    const NEXT = rtl ? 'ArrowLeft' : 'ArrowRight';
    const PREV = rtl ? 'ArrowRight' : 'ArrowLeft';

    // Escape closes an open panel wherever focus currently is (document-level listener)
    if (event.key === 'Escape') {
      if (this.#openItem) {
        const trigger = this.#openItem.querySelector('[data-vn-trigger]');
        const focusInside = this.contains(target);
        this.close();
        if (focusInside) {
          this.#suppressFocusOpen = true;
          trigger?.focus();
          window.setTimeout(() => (this.#suppressFocusOpen = false), 0);
        }
        event.preventDefault();
      }
      return;
    }

    // Everything below only applies to keys pressed inside the navigation
    if (!this.contains(target)) return;

    // --- top-level tabs
    if (target.hasAttribute('data-vn-trigger')) {
      const index = this.triggers.indexOf(target);
      if (event.key === NEXT || event.key === PREV) {
        event.preventDefault();
        const dir = event.key === NEXT ? 1 : -1;
        const nextTrigger = this.triggers[(index + dir + this.triggers.length) % this.triggers.length];
        nextTrigger?.focus();
      } else if (event.key === 'ArrowDown' || event.key === ' ') {
        const item = target.closest('[data-vn-item]');
        const first = item?.querySelector('[data-vn-rail], [data-vn-simple-link]');
        if (first) {
          event.preventDefault();
          this.open(item);
          first.focus();
        }
      }
      return;
    }

    // --- left rail
    if (target.hasAttribute('data-vn-rail') || target.hasAttribute('data-vn-see-all')) {
      const item = target.closest('[data-vn-item]');
      const rail = Array.from(item.querySelectorAll('[data-vn-see-all], [data-vn-rail]'));
      const index = rail.indexOf(target);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        rail[(index + dir + rail.length) % rail.length]?.focus();
      } else if (event.key === NEXT) {
        const pane = item.querySelector('[data-vn-pane]:not([hidden])');
        const first = pane?.querySelector('a');
        if (first) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        (event.key === 'Home' ? rail[0] : rail[rail.length - 1])?.focus();
      }
      return;
    }

    // --- right pane
    const pane = target.closest('[data-vn-pane]');
    if (pane) {
      const links = Array.from(pane.querySelectorAll('a'));
      const index = links.indexOf(target);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        links[(index + dir + links.length) % links.length]?.focus();
      } else if (event.key === PREV) {
        event.preventDefault();
        pane.closest('[data-vn-item]')?.querySelector('[data-vn-rail].is-active')?.focus();
      }
      return;
    }

    // --- simple dropdown list (e.g. ABOUT TREASURED)
    if (target.hasAttribute('data-vn-simple-link')) {
      const links = Array.from(target.closest('[data-vn-panel]').querySelectorAll('[data-vn-simple-link]'));
      const index = links.indexOf(target);
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        links[(index + dir + links.length) % links.length]?.focus();
      }
    }
  };

  /* ---- menu ⇄ drawer switching ---------------------------------------- */

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#syncMenuStyle(), RESIZE_DEBOUNCE);
  };

  /**
   * Horizon decides between the inline menu and the drawer with
   * `data-menu-style` on #header-component. The original logic relies on
   * <overflow-list>, which this menu does not use, so we measure overflow
   * ourselves and keep the same attribute contract.
   */
  #syncMenuStyle() {
    const header = document.getElementById('header-component');
    if (!header) return;

    if (isTouchDevice()) {
      header.dataset.menuStyle = 'drawer';
      return;
    }

    // setTimeout rather than requestAnimationFrame so this also runs in background tabs
    window.setTimeout(() => {
      const width = window.innerWidth;
      if (this.#drawerSwitchWidth !== null && width <= this.#drawerSwitchWidth) {
        header.dataset.menuStyle = 'drawer';
        return;
      }
      this.#drawerSwitchWidth = null;
      header.dataset.menuStyle = 'menu';

      const columns = header.querySelector('.header__row--top .header__columns');
      const overflows = columns ? columns.scrollWidth > columns.clientWidth + 1 : false;
      if (overflows) {
        header.dataset.menuStyle = 'drawer';
        this.#drawerSwitchWidth = width;
        this.close();
        return;
      }
      this.#measureNavStart();
    }, 0);
  }

  /**
   * Visual only: expose where the tab row starts (relative to the header row the
   * panel is positioned against) so the dropdown content can line up under the
   * first tab, like Vinted, instead of spreading across the viewport.
   */
  #measureNavStart() {
    const list = this.querySelector('.vn__list');
    const row = this.closest('.header__row');
    if (!list || !row) return;
    const l = list.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const start = isRTL() ? r.right - l.right : l.left - r.left;
    this.style.setProperty('--vn-nav-start', `${Math.max(0, Math.round(start))}px`);
  }
}

if (!customElements.get('vinted-menu')) {
  customElements.define('vinted-menu', VintedMenu);
}

/* =========================================================================
   Mobile drawer
   ========================================================================= */
class VintedDrawer extends HTMLElement {
  #stack = [];
  #lastFocus = null;

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.levels = Array.from(this.querySelectorAll('[data-vn-level]'));
    this.root = this.levels.find((l) => l.getAttribute('data-vn-level') === 'root') || this.levels[0];

    this.details?.addEventListener('toggle', this.#onToggle);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#onResize);

    this.#showLevel(this.root, { animate: false });
  }

  disconnectedCallback() {
    this.details?.removeEventListener('toggle', this.#onToggle);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#onResize);
    this.#unlockScroll();
  }

  get isOpen() {
    return !!this.details?.open;
  }

  openDrawer() {
    if (this.details) this.details.open = true;
  }

  closeDrawer() {
    if (this.details) this.details.open = false;
  }

  /** @param {string} id */
  go(id) {
    const level = this.levels.find((l) => l.getAttribute('data-vn-level') === id);
    if (!level) return;
    this.#stack.push(this.#current());
    this.#showLevel(level);
    this.#focusHeading(level);
  }

  back() {
    const prev = this.#stack.pop();
    if (!prev) {
      this.closeDrawer();
      return;
    }
    this.#showLevel(prev);
    this.#focusHeading(prev);
  }

  #current() {
    return this.levels.find((l) => l.classList.contains('is-active')) || this.root;
  }

  #showLevel(level, { animate = true } = {}) {
    for (const l of this.levels) {
      const active = l === level;
      const behind = !active && this.#stack.includes(l);
      l.classList.toggle('is-active', active);
      l.classList.toggle('is-behind', behind);
      if (active) {
        l.removeAttribute('inert');
        l.removeAttribute('aria-hidden');
      } else {
        l.setAttribute('inert', '');
        l.setAttribute('aria-hidden', 'true');
      }
      if (!animate) l.style.transition = 'none';
    }
    if (!animate) {
      // re-enable transitions on the next frame
      window.requestAnimationFrame(() => {
        for (const l of this.levels) l.style.transition = '';
      });
    }
    level.querySelector('.vn-drawer__scroll')?.scrollTo({ top: 0 });
  }

  #focusHeading(level) {
    const heading = level.querySelector('[data-vn-heading]');
    if (heading instanceof HTMLElement) {
      heading.setAttribute('tabindex', '-1');
      window.setTimeout(() => heading.focus({ preventScroll: true }), 60);
    }
  }

  #onToggle = () => {
    if (this.isOpen) {
      this.#lastFocus = document.activeElement;
      this.#stack = [];
      this.#showLevel(this.root, { animate: false });
      this.#lockScroll();
      this.#focusHeading(this.root);
    } else {
      this.#unlockScroll();
      this.#stack = [];
      if (this.#lastFocus instanceof HTMLElement && this.contains(this.#lastFocus)) {
        this.summary?.focus({ preventScroll: true });
      }
    }
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const go = target.closest('[data-vn-go]');
    if (go && this.contains(go)) {
      event.preventDefault();
      this.go(go.getAttribute('data-vn-go'));
      return;
    }
    if (target.closest('[data-vn-back]')) {
      event.preventDefault();
      this.back();
      return;
    }
    if (target.closest('[data-vn-close]')) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    // Following a real link closes the drawer so the page underneath is usable on back-nav
    const link = target.closest('a[href]');
    if (link && this.contains(link) && this.isOpen) {
      window.setTimeout(() => this.closeDrawer(), 150);
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      if (this.#stack.length) this.back();
      else this.closeDrawer();
    }
  };

  #onResize = () => {
    // If the layout switched back to the inline desktop menu, make sure the drawer is closed
    const header = document.getElementById('header-component');
    if (this.isOpen && header?.dataset.menuStyle === 'menu' && window.innerWidth >= 750) {
      this.closeDrawer();
    }
  };

  #lockScroll() {
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
  }
}

if (!customElements.get('vinted-drawer')) {
  customElements.define('vinted-drawer', VintedDrawer);
}
